import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PaperControl } from '@/module/Author/Paper/PaperControl';
import { AbstractMembershipSelectControl } from '@/module/Nomen/AbstractMembership/AbstractMembershipSelectControl';
import { ConferenceSelectControl } from '@/module/Nomen/Conference/ConferenceSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, TextInputControl } from '@/core/components';

import { AbstractSchema } from './model/AbstractSchema';

interface AbstractFormProps extends FormSubmit<AbstractSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function AbstractForm(props: AbstractFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit, showWosFields, showScopusFields } =
    props;
  const { t } = useTranslation();
  const form = useForm<AbstractSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <PaperControl
              isSubmitting={isSubmitting}
              showScopusFields={showScopusFields}
              showWosFields={showWosFields}
            />
            <ConferenceSelectControl<AbstractSchema>
              label={t('abstracts.form.fields.conference')}
              name="conference"
              isSubmitting={isSubmitting}
            />
            <TextInputControl<AbstractSchema>
              label={t('abstracts.form.fields.volumeIsbn')}
              name="volumeIsbn"
              isSubmitting={isSubmitting}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<AbstractSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                // TODO: possible improve
                <AbstractMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
